// Mobile Bluetooth (BLE) receipt printers via Web Bluetooth — e.g. a waiter's
// personal printer paired with their phone. Supported by Chrome on Android and
// desktop; not by Safari/iOS. Classic (SPP-only) Bluetooth printers are not reachable.

// GATT services used by common mobile ESC/POS printers.
export const BLE_PRINTER_SERVICES = [
  '000018f0-0000-1000-8000-00805f9b34fb',
  'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
  '49535343-fe7d-4ae5-8fa9-9fafd205e455',
  '0000ff00-0000-1000-8000-00805f9b34fb',
];
const CHUNK_SIZE = 180;

const knownDevices = new Map();

export function isWebBluetoothSupported() {
  return typeof navigator !== 'undefined' && Boolean(navigator.bluetooth);
}

export function chunkBytes(bytes, size = CHUNK_SIZE) {
  const chunks = [];
  for (let offset = 0; offset < bytes.length; offset += size) {
    chunks.push(bytes.slice(offset, offset + size));
  }
  return chunks;
}

// Must be called from a user gesture (button click).
export async function pairBluetoothPrinter() {
  if (!isWebBluetoothSupported()) {
    throw new Error('Dieser Browser unterstützt kein Web Bluetooth. Bitte Chrome auf Android verwenden.');
  }
  const device = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: BLE_PRINTER_SERVICES,
  });
  knownDevices.set(device.id, device);
  return { id: device.id, name: device.name || 'Bluetooth-Drucker' };
}

async function resolveDevice(deviceId) {
  if (knownDevices.has(deviceId)) return knownDevices.get(deviceId);
  if (isWebBluetoothSupported() && navigator.bluetooth.getDevices) {
    const devices = await navigator.bluetooth.getDevices();
    const device = devices.find((entry) => entry.id === deviceId);
    if (device) {
      knownDevices.set(device.id, device);
      return device;
    }
  }
  return null;
}

export async function isBluetoothPrinterAvailable(deviceId) {
  return Boolean(await resolveDevice(deviceId).catch(() => null));
}

async function findWritableCharacteristic(server) {
  for (const serviceId of BLE_PRINTER_SERVICES) {
    let service;
    try {
      service = await server.getPrimaryService(serviceId);
    } catch {
      continue;
    }
    const characteristics = await service.getCharacteristics();
    const writable = characteristics.find(
      (characteristic) => characteristic.properties.writeWithoutResponse || characteristic.properties.write,
    );
    if (writable) return writable;
  }
  throw new Error('Kein beschreibbarer Druckerkanal gefunden. Ist das ein BLE-Bondrucker?');
}

export async function printWithBluetooth(deviceId, bytes) {
  if (!isWebBluetoothSupported()) {
    throw new Error('Web Bluetooth ist auf diesem Gerät nicht verfügbar (nur Chrome auf Android).');
  }
  const device = await resolveDevice(deviceId);
  if (!device) {
    throw new Error('Bluetooth-Drucker nicht verbunden. Bitte in Einstellungen → Dieses Gerät auf „Verbinden“ tippen.');
  }
  const server = device.gatt.connected ? device.gatt : await device.gatt.connect();
  const characteristic = await findWritableCharacteristic(server);
  for (const chunk of chunkBytes(bytes)) {
    if (characteristic.properties.writeWithoutResponse && characteristic.writeValueWithoutResponse) {
      await characteristic.writeValueWithoutResponse(chunk);
      // Small pause so cheap printers' buffers don't overflow.
      await new Promise((resolve) => setTimeout(resolve, 20));
    } else {
      await characteristic.writeValue(chunk);
    }
  }
}
